import { MarkdownSerializer, MarkdownSerializerState } from "@tiptap/pm/markdown";
import { isSpacerParagraph } from "./proseMirrorNodePredicates";

export const MARKDOWN_SERIALIZER_PROFILES = Object.freeze({
  CLIPBOARD: "clipboard",
  ROUND_TRIP: "roundTrip",
});

const getStringMarkAttr = (mark, attrName) => {
  const value = mark?.attrs?.[attrName];
  return typeof value === "string" ? value : "";
};

const isSafeClipboardLink = (mark) => /^https?:\/\//i.test(getStringMarkAttr(mark, "href"));

const escapeLinkDestination = (href) => href.replace(/[\\()]/g, "\\$&");

const isPlainURL = (link, parent, index, side) => {
  const href = getStringMarkAttr(link, "href");
  const title = getStringMarkAttr(link, "title");
  if (title || !/^\w+:/.test(href)) return false;
  const content = parent.child(index + (side < 0 ? -1 : 0));
  if (!content.isText || content.text !== href || content.marks[content.marks.length - 1] !== link) return false;
  if (index === (side < 0 ? 1 : parent.childCount - 1)) return true;
  const next = parent.child(index + (side < 0 ? -2 : 1));
  return !link.isInSet(next.marks);
};

const createNodeSerializers = (profile) => {
  const roundTrip = profile === MARKDOWN_SERIALIZER_PROFILES.ROUND_TRIP;

  return {
    doc(state, node) {
      state.renderContent(node);
    },
    paragraph(state, node) {
      if (!roundTrip && isSpacerParagraph(node)) return;
      if (!node.childCount) {
        if (roundTrip) {
          // Markdown has no literal empty paragraphs; NBSP survives the paired
          // parser/serializer round trip.
          state.text("\u00a0", false);
        } else {
          return;
        }
      } else {
        state.renderInline(node);
      }
      state.closeBlock(node);
    },
    heading(state, node) {
      state.write(state.repeat("#", node.attrs.level) + " ");
      state.renderInline(node);
      state.closeBlock(node);
    },
    bulletList(state, node) {
      state.renderList(node, "  ", () => (roundTrip ? "* " : "- "));
    },
    orderedList(state, node) {
      const start = Number(node.attrs.start ?? node.attrs.order) || 1;
      state.renderList(node, "  ", (index) => `${start + index}. `);
    },
    listItem(state, node) {
      state.renderContent(node);
    },
    // A distinct round-trip marker keeps adjacent bullet and task lists separate.
    // The parser also accepts legacy "* [ ]" task lists.
    taskList(state, node) {
      state.renderList(node, "  ", (index) => {
        const checked = node.child(index).attrs.checked ? "x" : " ";
        return `- [${checked}] `;
      });
    },
    taskItem(state, node) {
      state.renderContent(node);
    },
    hardBreak(state, node, parent, index) {
      if (!roundTrip) {
        state.write("\n");
        return;
      }

      for (let i = index + 1; i < parent.childCount; i += 1) {
        if (parent.child(i).type !== node.type) {
          state.write("\\\n");
          return;
        }
      }
      // A trailing backslash is parsed as literal text at a block boundary.
      state.write("<br/>");
    },
    text(state, node) {
      if (roundTrip) {
        state.text(node.text);
        return;
      }

      const hasMarkdownFormatting = node.marks.some(
        (mark) => mark.type.name === "bold" || mark.type.name === "italic" || mark.type.name === "strike" || (mark.type.name === "link" && isSafeClipboardLink(mark)),
      );
      // Preserve existing clipboard output for unformatted prose while
      // escaping delimiters inside formatting generated by this serializer.
      state.text(node.text, hasMarkdownFormatting);
    },
    image(state, node) {
      if (!roundTrip) return;
      state.write(`![${state.esc(node.attrs.alt || "")}](${state.esc(node.attrs.src)}${node.attrs.title ? ` ${state.quote(node.attrs.title)}` : ""})`);
    },
  };
};

const createMarkSerializers = (profile) => {
  const roundTrip = profile === MARKDOWN_SERIALIZER_PROFILES.ROUND_TRIP;
  const emphasis = (delimiter) => ({
    open: delimiter,
    close: delimiter,
    mixable: true,
    expelEnclosingWhitespace: true,
  });

  const marks = {
    bold: emphasis("**"),
    italic: emphasis("*"),
    strike: emphasis("~~"),
    link: roundTrip
      ? {
          open(_state, mark, parent, index) {
            return isPlainURL(mark, parent, index, 1) ? "<" : "[";
          },
          close(state, mark, parent, index) {
            const href = getStringMarkAttr(mark, "href");
            const title = getStringMarkAttr(mark, "title");
            return isPlainURL(mark, parent, index, -1) ? ">" : `](${state.esc(href)}${title ? ` ${state.quote(title)}` : ""})`;
          },
          mixable: true,
        }
      : {
          open(_state, mark) {
            return isSafeClipboardLink(mark) ? "[" : "";
          },
          close(_state, mark) {
            return isSafeClipboardLink(mark) ? `](${escapeLinkDestination(getStringMarkAttr(mark, "href"))})` : "";
          },
          mixable: true,
        },
  };

  if (roundTrip) {
    Object.assign(marks, {
      underline: { open: "<u>", close: "</u>" },
      inserted: { open: "<inserted>", close: "</inserted>" },
      annotation: {
        open(_state, mark) {
          const annotationId = mark.attrs.annotationId;
          return annotationId ? `<annotation data-annotation-id="${annotationId}">` : "<annotation>";
        },
        close: "</annotation>",
      },
      insertion: { open: "<insertion>", close: "</insertion>" },
      deletion: { open: "<deletion>", close: "</deletion>" },
      highlight: {
        open(_state, mark) {
          const color = mark.attrs.color;
          return color != null ? `<mark data-color="${color}">` : "<mark>";
        },
        close: "</mark>",
      },
    });
  }

  return marks;
};

class FastMarkdownSerializerState extends MarkdownSerializerState {
  constructor(nodes, marks, options) {
    super(nodes, marks, options);
    this._chunks = [];
    this._tail = "";
  }

  atBlank() {
    return this._tail === "" || this._tail.endsWith("\n");
  }

  flushClose(size = 2) {
    if (!this.closed) return;
    if (!this.atBlank()) this._append("\n");
    if (size > 1) {
      const delimMin = this.delim.replace(/\s+$/, "");
      for (let i = 1; i < size; i += 1) this._append(`${delimMin}\n`);
    }
    this.closed = null;
  }

  ensureNewLine() {
    if (!this.atBlank()) this._append("\n");
  }

  write(content) {
    this.flushClose();
    if (this.delim && this.atBlank()) this._append(this.delim);
    if (content) this._append(content);
  }

  text(text, escape = true) {
    const lines = text.split("\n");
    for (let i = 0; i < lines.length; i += 1) {
      this.write();
      if (!escape && lines[i].startsWith("[") && this._endsWithUnescapedBang()) {
        this._escapeTrailingBang();
      }
      this._append(escape ? this.esc(lines[i], this.atBlockStart) : lines[i]);
      if (i !== lines.length - 1) this._append("\n");
    }
  }

  finish() {
    this.out = this._chunks.join("");
    return this.out;
  }

  _append(value) {
    if (!value) return;
    this._chunks.push(value);
    this._tail = `${this._tail}${value}`.slice(-2);
  }

  _endsWithUnescapedBang() {
    return this._tail.endsWith("!") && !this._tail.endsWith("\\!");
  }

  _escapeTrailingBang() {
    const lastIndex = this._chunks.length - 1;
    this._chunks[lastIndex] = `${this._chunks[lastIndex].slice(0, -1)}\\!`;
    this._tail = `${this._tail.slice(0, -1)}\\!`.slice(-2);
  }
}

class FastMarkdownSerializer extends MarkdownSerializer {
  serialize(content, options = {}) {
    const merged = Object.assign({}, this.options, options);
    const state = new FastMarkdownSerializerState(this.nodes, this.marks, merged);
    state.renderContent(content);
    return state.finish();
  }
}

export const createProseMirrorMarkdownSerializer = (profile) => {
  if (!Object.values(MARKDOWN_SERIALIZER_PROFILES).includes(profile)) {
    throw new Error(`Unknown Markdown serializer profile: ${profile}`);
  }

  return new FastMarkdownSerializer(createNodeSerializers(profile), createMarkSerializers(profile), {
    hardBreakNodeName: "hardBreak",
    strict: profile === MARKDOWN_SERIALIZER_PROFILES.ROUND_TRIP,
  });
};

export const serializeInlineToMarkdown = (serializer, node) => {
  const state = new FastMarkdownSerializerState(serializer.nodes, serializer.marks, serializer.options);
  state.renderInline(node);
  return state.finish();
};
